import React from "react";
import * as Sentry from "@sentry/react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
import { SUPABASE_URL } from '@/lib/supabase-config';

export function initSentry() {
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.info("[Sentry] No DSN configured — skipping initialization.");
    }
    return;
  }

  // Build trace propagation targets: localhost + Supabase API
  const tracePropagationTargets: (string | RegExp)[] = ["localhost"];
  if (SUPABASE_URL) {
    const escaped = SUPABASE_URL.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    tracePropagationTargets.push(new RegExp(`^${escaped}`));
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // "development" | "production"
    release: import.meta.env.VITE_APP_VERSION || undefined,

    // Send default PII (IP address, etc.) for richer issue context
    sendDefaultPii: true,

    // ── Tracing ──────────────────────────────────────────────────────────
    // Sample 10% of transactions in prod, 100% in dev
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Distributed tracing — attach sentry-trace headers to Supabase API calls
    tracePropagationTargets,

    // ── Session Replay ───────────────────────────────────────────────────
    // 10% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // ── Profiling ────────────────────────────────────────────────────────
    // Profile 100% of sessions in dev, 10% in prod (Chromium only)
    profileSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // ── Logs ─────────────────────────────────────────────────────────────
    enableLogs: true,

    // ── Integrations ─────────────────────────────────────────────────────
    integrations: [
      // React Router v6 aware browser tracing (parameterized route names)
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session Replay — record & replay user sessions for debugging
      Sentry.replayIntegration(),

      // Browser Profiling — function-level perf data (Chromium only, beta)
      Sentry.browserProfilingIntegration(),

      // Structured Logs — auto-capture console.warn and console.error to Sentry Logs
      Sentry.consoleLoggingIntegration({
        levels: ["warn", "error"],
      }),

      // User Feedback — embedded widget for users to report bugs
      Sentry.feedbackIntegration({
        colorScheme: "system",
        autoInject: false,
        showBranding: false,
        enableScreenshot: true,
        triggerLabel: "Report a Bug",
        formTitle: "Report a Bug",
        submitButtonLabel: "Send Report",
        messagePlaceholder: "What happened? What did you expect?",
      }),

      // HTTP Client — capture failed fetch/XHR responses (4xx/5xx) as errors
      Sentry.httpClientIntegration(),
    ],

    // Capture HTTP client errors (4xx/5xx responses)
    sendClientReports: true,

    // Filter out noisy or irrelevant errors
    beforeSend(event) {
      return event;
    },

    // Filter logs before sending (drop debug/trace in prod)
    beforeSendLog(log) {
      if (import.meta.env.PROD && (log.level === "debug" || log.level === "trace")) {
        return null;
      }
      return log;
    },

    // Ignore common browser extension & third-party errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "http://tt.epicplay.com",
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Common false positives
      "ResizeObserver loop",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      // Network errors that aren't actionable
      "Failed to fetch",
      "NetworkError",
      "Load failed",
    ],
  });
}

// ── User Context ───────────────────────────────────────────────────────────

/**
 * Set user context after authentication.
 * Call this when the user logs in.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  role?: string;
  dealership_id?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
  if (user.role) {
    Sentry.setTag("user_role", user.role);
  }
  if (user.dealership_id) {
    Sentry.setTag("dealership_id", user.dealership_id);
  }
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

// ── Error Capture ──────────────────────────────────────────────────────────

/**
 * Capture a custom error with optional extra context.
 */
export function captureError(
  error: Error | string,
  context?: Record<string, unknown>
) {
  if (typeof error === "string") {
    Sentry.captureMessage(error, {
      level: "error",
      extra: context,
    });
  } else {
    Sentry.captureException(error, {
      extra: context,
    });
  }
}

// ── Structured Logging ─────────────────────────────────────────────────────

/**
 * Structured logger — sends logs to Sentry with searchable attributes.
 *
 * Usage:
 *   import { logger } from "@/lib/sentry";
 *   logger.info("Order completed", { orderId: "123", total: 49.99 });
 *   logger.error("Payment failed", { reason: "card_declined" });
 *   logger.warn(logger.fmt`User ${userId} exceeded rate limit`);
 */
export const logger = Sentry.logger;

// ── Metrics ────────────────────────────────────────────────────────────────

/**
 * Custom metrics — track counts, gauges, and distributions in Sentry.
 *
 * Usage:
 *   import { metrics } from "@/lib/sentry";
 *   metrics.count("training_sessions_started", 1, { attributes: { scenario: "objection" } });
 *   metrics.distribution("call_duration", 245, { unit: "second" });
 *   metrics.gauge("active_users", 12);
 */
export const metrics = Sentry.metrics;

// ── Re-exports ─────────────────────────────────────────────────────────────

export { Sentry };
